import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiCheckCircle, FiXCircle } from "react-icons/fi";
import { decideApproval, isFinalApprovalDecision } from "../../_services/approval.service";
import { authenticatedIdentity } from "../../_services/apiFetch";
import {
    ApproveModal, RejectModal, T, btnStyleDanger, btnStyleSuccess,
} from "../Sales/SalesShared";

/* Bulk approve / reject for the Sales, Purchasing and Finance approval pages.
   Every document still goes through the normal single-document decision call,
   one after another, so the server checks each one exactly as before (the
   user's approval level, routing, status). A failure on one document never
   stops the others. */

// Approval lists mix document types, so they key by type + database id.
// A normal list holds one type, so its document number is enough.
export const rowKey = (row) =>
    row.recordType ? `${row.recordType}:${row.dbId}` : `doc:${row.id}`;

// Only documents this user can decide right now can be ticked.
export const isBulkSelectable = (row) =>
    row.status === "Pending for Approval" &&
    Boolean(row.canApprove && row.canReject) &&
    Boolean(row.recordType) &&
    row.dbId != null;

/* Tick state for the rows on screen. `canSelect(row)` says which rows get a
   box (null turns ticking off). Ticks are cleared whenever the rows change
   (page, filter, search or refresh), so what is ticked is always what is shown. */
export function useBulkSelection(rows, canSelect = isBulkSelectable) {
    const [checked, setChecked] = useState({});

    useEffect(() => {
        setChecked({});
    }, [rows]);

    const selectableRows = useMemo(
        () => (canSelect ? rows.filter(canSelect) : []),
        [rows, canSelect]
    );
    const selectedRows = useMemo(
        () => selectableRows.filter((row) => checked[rowKey(row)]),
        [selectableRows, checked]
    );

    const toggle = useCallback((row) => {
        const key = rowKey(row);
        setChecked((current) => ({ ...current, [key]: !current[key] }));
    }, []);
    const allChecked = selectableRows.length > 0 && selectedRows.length === selectableRows.length;
    const toggleAll = useCallback(() => {
        setChecked(
            allChecked
                ? {}
                : Object.fromEntries(selectableRows.map((row) => [rowKey(row), true]))
        );
    }, [allChecked, selectableRows]);
    const clear = useCallback(() => setChecked({}), []);

    return {
        selectableRows,
        selectedRows,
        canSelect: (row) => Boolean(canSelect?.(row)),
        isChecked: (row) => Boolean(checked[rowKey(row)]),
        toggle,
        allChecked,
        someChecked: selectedRows.length > 0 && !allChecked,
        toggleAll,
        clear,
    };
}

const checkboxStyle = { width: 16, height: 16, cursor: "pointer", accentColor: T.accent };

export function BulkSelectAllCell({ selection }) {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = selection.someChecked;
    }, [selection.someChecked]);
    return (
        <th style={{ width: 40 }}>
            <input
                ref={ref}
                type="checkbox"
                aria-label="Select all documents on this page"
                checked={selection.allChecked}
                disabled={selection.selectableRows.length === 0}
                onChange={selection.toggleAll}
                style={checkboxStyle}
            />
        </th>
    );
}

export function BulkSelectCell({ selection, row }) {
    if (!selection.canSelect(row)) return <td style={{ width: 40 }} />;
    return (
        <td style={{ width: 40 }} onClick={(event) => event.stopPropagation()}>
            <input
                type="checkbox"
                aria-label={`Select ${row.id}`}
                checked={selection.isChecked(row)}
                onChange={() => selection.toggle(row)}
                onKeyDown={(event) => event.stopPropagation()}
                style={checkboxStyle}
            />
        </td>
    );
}

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/* One banner message for the whole run. */
export function describeBulkOutcome({ action, succeeded, failed }) {
    const verb = action === "reject" ? "rejected" : "approved";
    const parts = [];
    if (succeeded.length) {
        let text = `${plural(succeeded.length, "document")} ${verb}`;
        if (action === "approve") {
            const waiting = succeeded.filter(({ result }) => !isFinalApprovalDecision(result)).length;
            if (waiting) text += ` (${waiting} now waiting for the next approval level)`;
        }
        parts.push(text);
    }
    if (failed.length) {
        parts.push(
            `${plural(failed.length, "document")} failed: ` +
            failed.map(({ row, error }) => `${row.id} (${error?.message || "unknown error"})`).join("; ")
        );
    }
    return {
        type: failed.length ? "danger" : "success",
        msg: `${parts.join(". ")}.`,
    };
}

/* Decides the documents one after another and reports what happened. */
export async function runBulkDecision(rows, action, reason = null, onProgress = null) {
    const startedAs = authenticatedIdentity();
    const succeeded = [];
    const failed = [];
    for (const [index, row] of rows.entries()) {
        if (authenticatedIdentity() !== startedAs) {
            failed.push({ row, error: new Error("The signed-in account changed") });
        } else {
            try {
                const result = await decideApproval(row.recordType, row.dbId, action, reason);
                if (result?.success === false) {
                    throw new Error(result.error || result.message || "Approval decision failed");
                }
                succeeded.push({ row, result });
            } catch (error) {
                failed.push({ row, error });
            }
        }
        onProgress?.(index + 1);
    }
    if (succeeded.length) {
        window.dispatchEvent(new CustomEvent("refreshPendingApprovalCounts"));
    }
    return { action, succeeded, failed };
}

/* The bar shown above the table once at least one document is ticked.
   `onComplete(outcome)` is called after every document has been processed. */
export function BulkApprovalBar({ selectedRows, onClear, onComplete }) {
    const [action, setAction] = useState(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);
    const mounted = useRef(true);
    const inFlight = useRef(false);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    // Documents are decided one by one; the progress text keeps the user informed.
    const target = useMemo(() => {
        if (!action) return null;
        const count = selectedRows.length;
        return { id: busy ? `${progress} of ${count} documents` : plural(count, "document") };
    }, [action, busy, progress, selectedRows.length]);

    if (selectedRows.length === 0 && !busy) return null;

    const confirm = async (reason) => {
        if (inFlight.current || !action) return;
        inFlight.current = true;
        setBusy(true);
        setProgress(0);
        const rows = [...selectedRows];
        const outcome = await runBulkDecision(rows, action, reason, (done) => {
            if (mounted.current) setProgress(done);
        });
        inFlight.current = false;
        if (mounted.current) {
            setBusy(false);
            setAction(null);
        }
        onComplete(outcome);
    };

    return (
        <div
            role="region"
            aria-label="Bulk approval"
            style={{
                display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                padding: "10px 16px", background: "#eff6ff",
                borderBottom: `1px solid ${T.border}`,
            }}
        >
            <span style={{ fontSize: 13, fontWeight: 600, color: T.accent }}>
                {plural(selectedRows.length, "document")} selected
            </span>
            <button
                type="button"
                onClick={() => setAction("approve")}
                disabled={busy}
                style={{ ...btnStyleSuccess, opacity: busy ? 0.6 : 1 }}
            >
                <FiCheckCircle size={13} /> Approve Selected
            </button>
            <button
                type="button"
                onClick={() => setAction("reject")}
                disabled={busy}
                style={{ ...btnStyleDanger, opacity: busy ? 0.6 : 1 }}
            >
                <FiXCircle size={13} /> Reject Selected
            </button>
            <button
                type="button"
                onClick={onClear}
                disabled={busy}
                style={{
                    background: "none", border: "none", color: T.textMuted,
                    fontSize: 13, fontWeight: 500, cursor: busy ? "not-allowed" : "pointer",
                }}
            >
                Clear selection
            </button>

            {action === "reject" ? (
                <RejectModal
                    target={target}
                    busy={busy}
                    onConfirm={confirm}
                    onCancel={() => !busy && setAction(null)}
                />
            ) : (
                <ApproveModal
                    target={target}
                    busy={busy}
                    onConfirm={() => confirm(null)}
                    onCancel={() => !busy && setAction(null)}
                />
            )}
        </div>
    );
}
